import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import { useTranslation } from "react-i18next";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/AdminDashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCalendarAlt, faThumbsUp, faBan, faExclamationCircle, faCreditCard, faHourglassHalf} from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import StatusLegend from '../components/AdminView/StatusLegend';
import FormatUrl from '../../../utils/UrlFormatter';
import { getClinicId } from '../../../utils/config';
import { toast } from "react-toastify";
// import MiniSpinner from '../../common/MiniSpinner';
import InvoicePreview from './InvoicePreview';

const localizer = momentLocalizer(moment);


const AdminDashboard = ({logged_in, user, logout}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const customMessages = {
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Summary',
  };

  const clinicId = getClinicId(user);
  const [appointments, setAppointments] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedPerPage] = useState(5);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false); 
  const [paymentMethod, setPaymentMethod] = useState("CA");
  const [showPreview, setShowPreview] = useState(false);
  const [isInvoiceConfirmed, setIsInvoiceConfirmed] = useState(false);
  const [isInvoiceAlreadyGenerated, setIsInvoiceAlreadyGenerated] = useState(false);
  // const [confirmedInvoice, setConfirmedInvoice] = useState(null);

  // const [isCancelling, setIsCancelling] = useState(false);
  // const [isApproving, setIsApproving] = useState(false);


  const fetchDoctors = useCallback(async (inputClinicId) => {
    const clinicId = getClinicId(user) ?? inputClinicId
    setLoadingDoctors(true); 
    try {
        const csrfResponse = await fetch(FormatUrl(`/csrf-token/`), {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token.');

        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const apiUrl = FormatUrl(`/get-doctors/?clinic_id=${clinicId}`);
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to fetch doctors.');

        const data = await response.json();

        if (data?.practitioners) {
            const formattedDoctors = data?.practitioners?.map(doctor => ({
                id: doctor.id,
                name: `${doctor.firstName} ${doctor.lastName}`,
                discipline: doctor.discipline || "Unknown",
            }));

            setDoctors(formattedDoctors); 
        } else {
            setDoctors([]);
        }
    } catch (error) {
        console.error("Error fetching doctors:", error.message);
        setDoctors([]);
    } finally {
        setLoadingDoctors(false);
    }
  }, [user]);


  const fetchBookings = useCallback(async (inputClinicId, discipline = null, practitionerId = null) => {
    const clinicId = getClinicId(user) ?? inputClinicId;
    setLoadingBookings(true); 
    try {
        const baseUrl = `/get-appointments/?clinic_id=${clinicId}`;
        let apiUrl = FormatUrl(baseUrl);

        if (discipline) apiUrl += `&discipline=${encodeURIComponent(discipline)}`;
        if (practitionerId) apiUrl += `&practitioner_id=${practitionerId}`;

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', 
        });

        if (response.ok) {
            const data = await response.json();

            if (data.status === "success") {
          
              // Transform fetched data for the Calendar component
              const transformedAppointments = data?.appointments?.map((appointment) => ({
                  id: appointment.appointment_id,
                  start: moment.utc(appointment.slot_start_time).toDate(), 
                  end: moment.utc(appointment.slot_end_time).toDate(),  
                  doctor_id: appointment.doctor_id,   
                  patient_id: appointment.patient_id,
                  patientName: `${appointment.patient_first_name || ''} ${appointment.patient_last_name || ''}`,
                  doctorName: `${appointment.doctor_first_name || ''} ${appointment.doctor_last_name || ''}`,
                  title: `${t("admin_dashboard.practitioner_id")}: ${appointment.doctor_id} - ${t("admin_dashboard.status")}: ${appointment.status}`,
                  status: appointment.status,
                  fee: appointment.base_price,
                  tax: appointment.tax_amount,
                  total: appointment.final_price,
                  patientPhone : appointment.patient_phone,
                  patientEmail : appointment.patient_email,
                  is_first: appointment.is_first,
                  station_number: appointment.station_number,
              }));
          
              setAppointments(transformedAppointments || []); // Update appointments state
          } else {
              console.error("Failed to fetch bookings:", data.error);
              setAppointments([]); // Reset on failure
          }
          
        } else {
            console.error('Failed to fetch bookings, HTTP Status:', response.status);
            setAppointments([]); // Reset state on non-OK response
        }
    } catch (error) {
        console.error("Error in fetchBookings:", error.message);
        setAppointments([]); // Reset state on error
    } finally {
        setLoadingBookings(false); // Hide loading indicator
    }
  }, [t, user]);
  
  useEffect(() => {
    if (clinicId) {
        fetchDoctors(clinicId);
        fetchBookings(clinicId);
    }
  }, [clinicId, fetchBookings, fetchDoctors]);

  useEffect(() => {
    if (clinicId && (selectedDiscipline || selectedDoctor)) {
        const practitionerId = doctors.find((doctor) => doctor.name === selectedDoctor)?.id;

        fetchBookings(clinicId, selectedDiscipline, practitionerId); // Fetch filtered bookings
    }
  }, [clinicId, selectedDiscipline, selectedDoctor, doctors, fetchBookings]);

  // Filter appointments
  const filteredAppointments = useMemo(() => {
  
    const latestAppointments = {};
    appointments.forEach((appointment) => {
      const { id, start } = appointment;
  
      // Keep the latest version based on 'start' time
      if (!latestAppointments[id]) {
        latestAppointments[id] = appointment;
      } else {
        const existingTime = new Date(latestAppointments[id].start);
        const currentTime = new Date(start);
        if (currentTime > existingTime) {
          latestAppointments[id] = appointment;
        }
      }
    });
  
    // Apply doctor filter only (no status filtering for List View)
    const finalAppointments = Object.values(latestAppointments).filter((appointment) => {
      return selectedDoctor ? appointment.doctor_id === selectedDoctor : true;
    });
  
    return finalAppointments;
  }, [appointments, selectedDoctor]);
  
  
  const calendarAppointments = useMemo(() => {

  
    const latestAppointments = {};
    appointments.forEach((appointment) => {
      const { id, start } = appointment;
  
      // Keep the latest version based on 'start' time
      if (!latestAppointments[id]) {
        latestAppointments[id] = appointment;
      } else {
        const existingTime = new Date(latestAppointments[id].start);
        const currentTime = new Date(start);
        if (currentTime > existingTime) {
          latestAppointments[id] = appointment;
        }
      }
    });
  
    // Apply both status and doctor filters for Calendar View
    const statusFilteredAppointments = Object.values(latestAppointments).filter((appointment) => {
      const matchesStatus =
        statusFilter === 'All' ||
        (statusFilter === 'booked' && appointment.status === 'BO') ||
        (statusFilter === 'approved' && appointment.status === 'AP') ||
        (statusFilter === 'completed' && appointment.status === 'CO') || 
        (statusFilter === 'cancelled' && appointment.status === 'CA') ||
        (statusFilter === 'available' && appointment.status === 'AV')||
        (statusFilter === 'paymentwithoutprac' && appointment.status === 'PW')||
        (statusFilter === 'paymentcomplete' && appointment.status === 'PC');
  
      const matchesDoctor = selectedDoctor ? appointment.doctor_id === selectedDoctor : true;
  
      return matchesStatus && matchesDoctor;
    });
  
    return statusFilteredAppointments;
  }, [appointments, selectedDoctor, statusFilter]);
  

  // Sort appointments
  const sortedAppointments = useMemo(() => {
    const sorted = [...filteredAppointments];
    sorted.sort((a, b) => new Date(a.start) - new Date(b.start));
    return sorted;
  }, [filteredAppointments]);

 // Filter 'BO' (Booked) appointments before pagination
  const bookedAppointments = sortedAppointments.filter(
    (appointment) => appointment.status === "BO"
  );

  // Calculate total pages based on 'BO' appointments only
  const totalPages = Math.max(Math.ceil(bookedAppointments.length / itemsPerPage), 1);

  // Ensure currentPage doesn't exceed totalPages
  const validCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (validCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Set the paginatedAppointments
  const paginatedAppointments = bookedAppointments.slice(startIndex, endIndex);

  // Update currentPage dynamically
  useEffect(() => {
    if (currentPage !== validCurrentPage) {
      setCurrentPage(validCurrentPage);
    }
  }, [validCurrentPage, currentPage]);

  // Pagination controls
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage * itemsPerPage < sortedAppointments.length) setCurrentPage(currentPage + 1);
  };

  const completedAppointments = sortedAppointments
  .filter(app => app.status === "CO")
  .sort((a, b) => new Date(a.start) - new Date(b.start));

  const totalCompletedPages = Math.max(Math.ceil(completedAppointments.length / completedPerPage), 1);
  const paginatedCompleted = completedAppointments.slice(
    (completedPage - 1) * completedPerPage,
    completedPage * completedPerPage
  );


  const handleApproveAppointment = async (appointmentId, newStatus) => {
    try {
      // Fetch CSRF Token
      const csrfApiUrl = FormatUrl('/csrf-token/');
      const csrfResponse = await fetch(csrfApiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
  
      if (!csrfResponse.ok) {
        throw new Error('Failed to fetch CSRF token');
      }
  
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
  
      // Call Approve API
      const approveApiUrl = FormatUrl('/approve-appointment/');
  
      const formData = new URLSearchParams();
      formData.append('appointment_id', appointmentId);
      formData.append('clinic_id', clinicId);
  
      const response = await fetch(approveApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });

  
      if (response.ok) {
        const result = await response.json();
  
        if (result.status === 'AP') {
          toast.success("Appointment approved!");
        
          setTimeout(() => {
            setAppointments((prev) =>
              prev.map((app) =>
                app.id === appointmentId ? { ...app, status: 'AP' } : app
              )
            );
          }, 800);
        } else {
          console.error("Failed to approve appointment:", result.error || "No error message");
        }
        
      } else {
        console.error('Approve API failed with HTTP Status:', response.status);
      }
    } catch (error) {
      console.error("Error in handleApproveAppointment:", error.message);
      toast.error(t("admin_dashboard.approve_failure"));
    }
  };
  
  const handleCancelAppointment = async (appointmentId) => {
    try {
      // Fetch CSRF Token
      const csrfApiUrl = FormatUrl('/csrf-token/');
      const csrfResponse = await fetch(csrfApiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
  
      if (!csrfResponse.ok) {
        throw new Error('Failed to fetch CSRF token');
      }
  
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
  
      // Call the Cancel API
      const cancelApiUrl = FormatUrl('/cancel-appointment/');
  
      const formData = new URLSearchParams();
      formData.append('appointment_id', appointmentId);
      formData.append('clinic_id', clinicId);
  
      const response = await fetch(cancelApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error(`Cancel API failed with status ${response.status}`);
      }
  
      const result = await response.json();
  
      if (result.status === 'CA') {
        toast.success("Booking has been cancelled.");
      
        setTimeout(() => {
          const updatedAppointments = appointments.map((app) =>
            app.id === appointmentId ? { ...app, status: 'CA' } : app
          );
          setAppointments(updatedAppointments);
          closeModal();
        }, 800); // Slightly longer delay ensures toast renders before UI change
      } else {
        toast.error(result.error || "Failed to cancel appointment.");
      }
      
  
    } catch (error) {
      console.error("Error in handleCancelAppointment:", error.message);
      toast.error(t("admin_dashboard.cancel_failure"));
    }
  };
  
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setPaymentMethod("CA");
  };

  const handleDisciplineClick = (discipline) => {
    if (selectedDiscipline === discipline) {
      setSelectedDiscipline(null); // Collapse if same discipline is clicked
    } else {
      setSelectedDiscipline(discipline); // Expand new discipline
    }
  };


  const handleConfirmPayment = async () => {
    try {
      if (!selectedEvent || !selectedEvent.id || !paymentMethod) {
        toast.error("Missing required information.");
        return;
      }
      
      const allowedPaymentMethods = ["CA", "DC", "CC", "ET"];
      if (!allowedPaymentMethods.includes(paymentMethod)) {
        toast.error("Invalid payment method selected.");
        return;
      }
      // Fetch CSRF token
      const csrfResponse = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
  
      if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token');
  
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
  
      // Prepare form data
      const formData = new URLSearchParams();
      formData.append("clinic_id", getClinicId(user));
      formData.append("appointment_id", selectedEvent.id);
      formData.append("payment_method", paymentMethod);
  
      // Make POST request
      const response = await fetch(FormatUrl('/payment-completed-appointment/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });
  
      if (!response.ok) throw new Error("Failed to confirm payment");
  
      const result = await response.json();
  
      if (result.status === "PC" || result.status === "PW") {
        toast.success("Payment confirmed.");
      
        const updatedAppointments = appointments.map((app) =>
          app.id === selectedEvent.id ? { ...app, status: result.status } : app
        );
        setAppointments(updatedAppointments);
      
        setTimeout(() => {
          closeModal();
        }, 500); // let toast show first
      } else {
        toast.error(result.error || "Unknown error during confirmation");
      }
      
    } catch (error) {
      console.error("Payment confirmation error:", error.message);
      toast.error("Payment confirmation failed.");
    }
  };
  

  useEffect(() => {
    // Extract role and clinic_id from the current URL
    const urlSegments = window.location.pathname.split("/");
    const currentRole = urlSegments[urlSegments.length - 2];
    const currentClinicId = urlSegments[urlSegments.length - 1];

    // If role or clinic_id does not match, redirect to /dashboard
    if (currentRole !== user?.role || currentClinicId !== String(getClinicId(user))) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleInvoiceConfirm = (invoiceData) => {
    toast.success("Invoice confirmed successfully!");
    setShowPreview(false);              
    setIsInvoiceConfirmed(true);     
    setIsInvoiceAlreadyGenerated(true);   
    // setConfirmedInvoice(invoiceData);
  };
  
  useEffect(() => {
    const checkInvoiceGenerated = async () => {
      if (!selectedEvent?.id) return;
  
      try {
        const csrfRes = await fetch(FormatUrl(`/csrf-token/`), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
  
        const { csrfToken } = await csrfRes.json();
  
        const res = await fetch(
          FormatUrl(`/is-invoice-generated/?appointment_id=${selectedEvent.id}`),
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
          }
        );
  
        const data = await res.json();
        const isConfirmed = data.status === true;

        setIsInvoiceAlreadyGenerated(isConfirmed);
        setIsInvoiceConfirmed(isConfirmed);
      } catch (error) {
        console.error('Error checking invoice status:', error);
      }
    };
  
    checkInvoiceGenerated();
  }, [selectedEvent]);
  

  return (
    <>
      <div className="admin-dashboard-container">
        {/* Sidebar for Disciplines and Doctors */}
        <div className="sidebar">
          <h3>{t("admin_dashboard.disciplines")}</h3>

          {/* All Option */}
          <button
            className={`discipline-button ${!selectedDiscipline && !selectedDoctor ? 'selected' : ''}`}
            onClick={() => {
              setSelectedDiscipline('');
              setSelectedDoctor('');
            }}
          >
            {t("admin_dashboard.all")}
          </button>

          {loadingDoctors ? (
            <div className="loading-indicator">{t("admin_dashboard.loading_appointments")}</div>
          ) : Array.isArray(doctors) && doctors.length > 0 ? (
            [...new Set(doctors.map(doctor => doctor.discipline))].map(discipline => (
              <div key={discipline} className="discipline-section">
                <button
                  className={`discipline-button ${selectedDiscipline === discipline ? 'selected' : ''}`}
                  onClick={() => handleDisciplineClick(discipline)}
                >
                  {discipline}
                </button>
                {selectedDiscipline === discipline && (
                  <ul className="doctor-list">
                    {doctors
                      .filter(doctor => doctor.discipline === discipline)
                      .map(doctor => (
                        <li key={doctor.id}>
                          <button
                            className={`doctor-button ${selectedDoctor === doctor.id ? 'selected' : ''}`}
                            onClick={() => setSelectedDoctor(doctor.id)}
                          >
                            {doctor.name}
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            ))
          ) : (
            <p>{t("admin_dashboard.no_practitioners")}</p>
          )}
        </div>


        {/* Main Dashboard Section */}
        <div className="main-dashboard">
          <h2 style={{ color: 'var(--clr-grey-1)' }}>{t("admin_dashboard.title")} - {user.clinic_name}</h2>

          {/* Filters and Search */}
          <div className="admin-controls">
          <div className="admin-filter">
            <button onClick={() => setStatusFilter('All')} className={statusFilter === 'All' ? 'active' : ''}>{t("admin_dashboard.all")}</button>
            <button onClick={() => setStatusFilter('booked')} className={statusFilter === 'booked' ? 'active' : ''}>
              <FontAwesomeIcon icon={faCalendarAlt} /> {t("admin_dashboard.booked")}
            </button>
            <button onClick={() => setStatusFilter('approved')} className={statusFilter === 'approved' ? 'active' : ''}>
              <FontAwesomeIcon icon={faThumbsUp} /> {t("admin_dashboard.approved")}
            </button>
            <button onClick={() => setStatusFilter('cancelled')} className={statusFilter === 'cancelled' ? 'active' : ''}>
              <FontAwesomeIcon icon={faBan} /> {t("admin_dashboard.cancelled")}
            </button>
            {/* <button onClick={() => setStatusFilter('available')} className={statusFilter === 'available' ? 'active' : ''}>
              <FontAwesomeIcon icon={faCheckCircle} /> Available
            </button> */}
            <button onClick={() => setStatusFilter('completed')} className={statusFilter === 'completed' ? 'active' : ''}>
              <FontAwesomeIcon icon={faCheckCircle} /> {t("admin_dashboard.completed")}
            </button>
            <button onClick={() => setStatusFilter('paymentcomplete')} className={statusFilter === 'paymentcomplete' ? 'active' : ''}>
              <FontAwesomeIcon icon={faCreditCard} /> {t("admin_dashboard.payment_completed")}
            </button>
            <button onClick={() => setStatusFilter('paymentwithoutprac')} className={statusFilter === 'paymentwithoutprac' ? 'active' : ''}>
              <FontAwesomeIcon icon={faHourglassHalf} /> {t("admin_dashboard.payment_completed_practitioner_incomplete")}
            </button>

          </div>
          </div>
          
          <StatusLegend/>

          {/* Calendar View */}
          <div className="admin-calendar">
            {loadingBookings ? (
              <div className="loading-indicator">{t("admin_dashboard.loading_appointments")}</div>
            ) : (
                  <Calendar
                      localizer={localizer}
                      events={calendarAppointments.map(app => ({
                        ...app,
                        id: app.id,
                        start: app.start, 
                        end: app.end,     
                        // title: `Doctor ID: ${app.doctor_id || app.doctorId || 'N/A'} - Status: ${app.status}`, 
                        title: `${t("admin_dashboard.practitioner")}: ${app.doctorName || t("admin_dashboard.na")}, ${t("admin_dashboard.patient")}: ${app.patientName || t("admin_dashboard.na")}, ${t("admin_dashboard.status")}: ${app.status}`
                      }))}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: 600 }}
                      views={['month', 'week', 'day', 'agenda']} 
                      messages={customMessages}
                      eventPropGetter={event => {
                        let background = 'linear-gradient(135deg, #e0e0e0, #bdbdbd)';
                        let color = 'white'; // Default text color
                      
                        switch (event.status) {
                          case 'AV': 
                            background = 'linear-gradient(135deg, #48a9a6, #3a8f8a)';
                            break;
                          case 'BO': // Booked
                            background = 'linear-gradient(135deg, #e07a5f, #b95441)'; 
                            break;
                          case 'AP': // Approved
                            background = 'linear-gradient(135deg, #f4d35e, #f6bd60)';
                            break;
                          case 'CA': // Cancelled
                            background = 'linear-gradient(135deg, #a0aec0, #718096)'; 
                            color = '#2d3748'; // Dark text for better readability
                            break;
                          case 'CO': // Completed
                            background = 'linear-gradient(135deg, #1e88e5, #1565c0)';
                            break;
                            case 'PC':
                           background = 'linear-gradient(135deg, #4caf50, #087f23)';
                            break;
                            case 'PW': // Pending with Waitlist
                            background = 'linear-gradient(135deg, #9c27b0, #7b1fa2)';
                            break;

                          default:
                            background = 'linear-gradient(135deg, #e0e0e0, #bdbdbd)';
                        }
                      
                        return {
                          style: {
                            background,
                            color,
                            borderRadius: '5px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Visual effect
                            border: '1px solid #ccc', // Optional border
                          },
                        };
                      }}
                      
                      onSelectEvent={handleEventClick}
                      components={{
                        event: ({ event }) => (
                          <div>
                            <div
                              data-tooltip-id={`tooltip-${event.id}`} // Unique tooltip ID
                              data-tooltip-content={`
                                  ${t("admin_dashboard.practitioner")}: ${event.doctorName} | 
                                  ${t("admin_dashboard.patient")}: ${event.patientName || t("admin_dashboard.na")} | 
                                  ${t("admin_dashboard.time")}: ${moment(event.start).format('h:mm A')} - ${moment(event.end).format('h:mm A')} | 
                                  ${t("admin_dashboard.status")}: ${event.status}
                                `}
                            >
                              {/* Display Icons Based on Status */}
                              {(() => {
                                switch (event.status) {
                                  case 'AV': // Available
                                    return <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px', color: '#3a8f8a' }} />;
                                  case 'BO': // Booked
                                    return <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '8px', color: '#b95441' }} />;
                                  case 'AP': // Approved
                                    return <FontAwesomeIcon icon={faThumbsUp} style={{ marginRight: '8px', color: '#d4a411' }} />;
                                  case 'CA': // Cancelled
                                    return <FontAwesomeIcon icon={faBan} style={{ marginRight: '8px', color: '#718096' }} />;
                                  case 'CO': // Completed
                                    return <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px', color: '#d4a411' }} />;
                                  case 'PC': // Payment Completed
                                    return <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px', color: '#1565c0' }} />;
                                  case 'PW': // Payment Completed
                                  return <FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '8px', color: '#f59e0b'  }} />;
                                  default: // Unknown Status
                                    return <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '8px', color: '#bdbdbd' }} />;
                                }
                              })()}
                              <strong>{`${event.doctorName}`}</strong>
                            </div>
                            {/* Tooltip */}
                            <Tooltip id={`tooltip-${event.id}`} place="top" />
                          </div>
                        ),
                      }}
                    />
            )}
          </div>

          {/* Appointment List View */}
            <div className="admin-appointments-container split-layout">

              {/* LEFT: Booked Appointments */}
              <div className="admin-appointments-list booked-section">
                <h3 style={{fontSize:'1.25rem'}}>{t("admin_dashboard.booked_appointments")}</h3>
                <ul>
                  {paginatedAppointments.length > 0 ? (
                    paginatedAppointments.map((appointment) => (
                      <li key={appointment.id}>
                        <div className="appointment-info">
                          <strong>{`${t("admin_dashboard.practitioner")}: ${appointment.doctorName || t("admin_dashboard.na")}`}</strong> |{' '}
                          <strong>{`${t("admin_dashboard.patient")}: ${appointment.patientName || ''}`}</strong> |{' '}
                          {moment(appointment.start).format('MMMM Do, h:mm A')} - {t("admin_dashboard.status")}: {appointment.status}

                          <div className="admin-appointment-actions" style={{ marginTop: '1rem' }}>
                            <button
                              onClick={() => handleApproveAppointment(appointment.id, 'approved')}
                              className="approve-btn"
                            >
                              {t("admin_dashboard.approve")}
                            </button>
                            <button
                              onClick={() => handleCancelAppointment(appointment.id)}
                              className="cancel-btn"
                            >
                              {t("admin_dashboard.cancel")}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="no-appointments">{t("admin_dashboard.no_appointments")}</li>
                  )}
                </ul>

                {/* Pagination */}
                <div className="pagination-controls">
                  <button onClick={handlePrevPage} disabled={currentPage === 1}>
                    {t("admin_dashboard.previous")}
                  </button>
                  <span>
                    {t("admin_dashboard.page")} {currentPage} {t("admin_dashboard.of")} {totalPages || 1}
                  </span>
                  <button onClick={handleNextPage} disabled={currentPage >= totalPages}>
                    {t("admin_dashboard.next")}
                  </button>
                </div>
              </div>

              {/* RIGHT: Completed Appointments */}
              <div className="admin-appointments-list completed-section">
                <h3  style={{fontSize:'1.25rem'}}>{t("admin_dashboard.completed_appointments")}</h3>

                {paginatedCompleted.length > 0 ? (
                    paginatedCompleted.map((appointment) => (
                      <div className="completed-card" key={appointment.id}>
                      <p style={{ fontWeight: 'bold' }}>
                        Prac: {appointment.doctorName || "N/A"} | Patient: {appointment.patientName || "N/A"}
                      </p>
                      <button
                        className="confirm-payment-btn"
                        onClick={() => setSelectedEvent(appointment)}
                      >
                        {t("admin_dashboard.confirm_payment")}
                      </button>
                    </div>
                    
                    ))
                  ) : (
                    <p className="no-appointments">{t("admin_dashboard.no_completed_tasks")}</p>
                  )}

                  {/* Pagination for Completed Appointments */}
                  <div className="pagination-controls">
                    <button
                      onClick={() => setCompletedPage(prev => Math.max(prev - 1, 1))}
                      disabled={completedPage === 1}
                    >
                      {t("admin_dashboard.previous")}
                    </button>
                    <span>
                      {t("admin_dashboard.page")} {completedPage} {t("admin_dashboard.of")} {totalCompletedPages}
                    </span>
                    <button
                      onClick={() => setCompletedPage(prev => Math.min(prev + 1, totalCompletedPages))}
                      disabled={completedPage >= totalCompletedPages}
                    >
                      {t("admin_dashboard.next")}
                    </button>
                  </div>
              </div>
            </div>


          {/* Modal for Event Details */}
          {selectedEvent && (
              <div className="modal-custom-admin">
                <div className="modal-content-custom-admin">
                  {selectedEvent.status === "CO" ? (
                    <>
                      <h3>{t("admin_dashboard.confirm_payment")}</h3>

                      <p><strong>{t("admin_dashboard.patient")}:</strong> {selectedEvent.patientName || "N/A"}</p>
                      <p><strong>{t("admin_dashboard.practitioner")}:</strong> {selectedEvent.doctorName || "N/A"}</p>
                      {selectedEvent?.station_number && (
                        <p>
                          <strong>Room:</strong>{" "}
                          <span className="room-highlight">{`Room ${selectedEvent.station_number}`}</span>
                        </p>
                      )}

                      <p><strong>{t("admin_dashboard.time")}:</strong> {moment(selectedEvent.start).format("MMMM Do, h:mm A")}</p>
                      

                      {selectedEvent?.patient_id && selectedEvent?.doctor_id && (
                        <div className="timeline-cta-box">
                          <p className="timeline-cta-text">
                            To see patient’s appointment history and timeline –{" "}
                            <span
                              className="timeline-cta-link"
                              onClick={() =>
                                window.open(
                                  `/patient-timeline/${selectedEvent.patient_id}`,
                                  '_blank',
                                  'noopener,noreferrer'
                                )
                              }
                            >
                              Click here
                            </span>

                          </p>
                        </div>
                      )}

                      {/* Collapsible Invoice Preview */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                          <span style={{ fontWeight: 600, color: '#d97706' }}>
                            Preview and Confirm Invoice here –
                          </span>
                      <button
                        className="invoice-preview-toggle-btn"
                        onClick={() => setShowPreview(!showPreview)}
                      >
                        {showPreview ? "Hide Invoice Preview ▲" : "Show Invoice Preview ▼"}
                      </button>
                      </div>
                      <div className={`invoice-preview-collapsible ${showPreview ? 'open' : ''}`}>
                      
                          <InvoicePreview
                            selectedEvent={selectedEvent}
                            clinicId={clinicId}
                            onConfirm={handleInvoiceConfirm}
                            isInvoiceAlreadyGenerated={isInvoiceAlreadyGenerated}
                          />

                      </div>
                      <label htmlFor="payment-method">{t("admin_dashboard.payment_method")}:</label>
                      <select
                        id="payment-method"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="CA">{t("admin_dashboard.payment_method_cash")}</option>
                        <option value="DC">{t("admin_dashboard.payment_method_debit")}</option>
                        <option value="CC">{t("admin_dashboard.payment_method_credit")}</option>
                        <option value="ET">{t("admin_dashboard.payment_method_etransfer")}</option>
                      </select>
  
                      {/* <div className="payment-breakdown">
                        <p>
                          <strong>{t("admin_dashboard.appointment_fee")}:</strong>{' '}
                          ${confirmedInvoice ? confirmedInvoice.amount.toFixed(2) : Number(selectedEvent.fee || 0).toFixed(2)}
                        </p>
                        <p>
                          <strong>{t("admin_dashboard.tax")}:</strong>{' '}
                          ${confirmedInvoice ? confirmedInvoice.tax_amount.toFixed(2) : Number(selectedEvent.tax || 0).toFixed(2)}
                        </p>
                        <p>
                          <strong>{t("admin_dashboard.total_amount")}:</strong>{' '}
                          ${confirmedInvoice ? confirmedInvoice.total_amount.toFixed(2) : Number(selectedEvent.total || 0).toFixed(2)}
                        </p>
                      </div> */}

                      {selectedEvent.status === "CO" && !isInvoiceConfirmed && (
                        <p className="invoice-hint-text">
                          Please confirm the invoice to enable both download and payment options.
                        </p>
                      )}
                      <div className="modal-btns-admin">
                       {selectedEvent?.patient_id && selectedEvent?.doctor_id && (
                          <button
                            className="view-intake-btn-admin"
                            onClick={() =>
                              window.open(
                                `/view-intake/${selectedEvent.patient_id}/${selectedEvent.doctor_id}`,
                                "_blank"
                              )
                            }
                          >
                            View Intake Form
                          </button>
                         )}
                        <button
                          className="download-invoice-btn-admin"
                          onClick={() => {
                            const url = FormatUrl(`/download-appointment-invoice/${selectedEvent.id}/`);
                            window.open(url, '_blank');
                          }}
                          disabled={!isInvoiceConfirmed} 
                        >
                          {t("admin_dashboard.download_invoice")}
                        </button>

                        <button className="close-btn-admin" onClick={closeModal}>
                          {t("admin_dashboard.close")}
                        </button>

                        <button
                          className="confirm-payment-btn-admin"
                          onClick={handleConfirmPayment}
                          disabled={!isInvoiceConfirmed} 
                        >
                          {t("admin_dashboard.submit_payment")}
                        </button>

                      </div>

                    </>
                  ) : (
                    <>
                      <h3>{t("admin_dashboard.appointment_details")}</h3>
                      {["BO", "AP"].includes(selectedEvent.status) && selectedEvent.is_first && (
                        <div className="corner-ribbon-v2">First Visit</div>
                      )}

                      <p><strong>{t("admin_dashboard.patient")}:</strong> {selectedEvent.patientName || "N/A"}</p>
                      <p><strong>{t("admin_dashboard.patient_email")}:</strong> {selectedEvent.patientEmail || "N/A"}</p>
                      <p><strong>{t("admin_dashboard.patient_phone")}:</strong> {selectedEvent.patientPhone || "N/A"}</p>
                      <p><strong>{t("admin_dashboard.practitioner")}:</strong> {selectedEvent.doctorName || "N/A"}</p>
                      <p><strong>{t("admin_dashboard.status")}:</strong> {selectedEvent.status || "Unknown"}</p>
                      {selectedEvent?.station_number && (
                        <p>
                          <strong>Room:</strong>{" "}
                          <span className="room-highlight">{`Room ${selectedEvent.station_number}`}</span>
                        </p>
                      )}

                      <p><strong>{t("admin_dashboard.time")}:</strong> {moment(selectedEvent.start).format("MMMM Do, h:mm A")} - {moment(selectedEvent.end).format("h:mm A")}</p>
                      

                      {selectedEvent?.patient_id && selectedEvent?.doctor_id && (
                        <div className="timeline-cta-box">
                          <p className="timeline-cta-text">
                            To see patient’s appointment history and timeline –{" "}
                            <span
                              className="timeline-cta-link"
                              onClick={() =>
                                window.open(
                                  `/patient-timeline/${selectedEvent.patient_id}`,
                                  '_blank',
                                  'noopener,noreferrer'
                                )
                              }
                            >
                              Click here
                            </span>
                              </p>
                            </div>
                          )}
                     {/* Collapsible Invoice Preview for AP */}
                     {selectedEvent.status === "AP" && (
                        <>
                          {moment().isSameOrAfter(moment(selectedEvent.start)) ? (
                          // {moment().isBefore(moment(selectedEvent.start)) ? ( 
                            <>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '1rem',
                                  marginBottom: '1rem'
                                }}
                              >
                                <span style={{ fontWeight: 600, color: '#d97706' }}>
                                  Preview and Confirm Invoice here –
                                </span>
                                <button
                                  className="invoice-preview-toggle-btn"
                                  onClick={() => setShowPreview(!showPreview)}
                                >
                                  {showPreview ? "Hide Invoice Preview ▲" : "Show Invoice Preview ▼"}
                                </button>
                              </div>

                              <div className={`invoice-preview-collapsible ${showPreview ? 'open' : ''}`}>
                             
                                <InvoicePreview
                                  selectedEvent={selectedEvent}
                                  clinicId={clinicId}
                                  onConfirm={handleInvoiceConfirm}
                                  isInvoiceAlreadyGenerated={isInvoiceAlreadyGenerated}
                                />
                        
                              </div>

                                {/* Always show payment section */}
                                <label htmlFor="payment-method">{t("admin_dashboard.payment_method")}:</label>
                                <select
                                  id="payment-method"
                                  value={paymentMethod}
                                  onChange={(e) => setPaymentMethod(e.target.value)}
                                >
                                  <option value="CA">{t("admin_dashboard.payment_method_cash")}</option>
                                  <option value="DC">{t("admin_dashboard.payment_method_debit")}</option>
                                  <option value="CC">{t("admin_dashboard.payment_method_credit")}</option>
                                  <option value="ET">{t("admin_dashboard.payment_method_etransfer")}</option>
                                </select>

                                {/* <div className="payment-breakdown">
                                  <p><strong>{t("admin_dashboard.appointment_fee")}:</strong> ${confirmedInvoice?.amount?.toFixed(2) || Number(selectedEvent.fee || 0).toFixed(2)}</p>
                                  <p><strong>{t("admin_dashboard.tax")}:</strong> ${confirmedInvoice?.tax_amount?.toFixed(2) || Number(selectedEvent.tax || 0).toFixed(2)}</p>
                                  <p><strong>{t("admin_dashboard.total_amount")}:</strong> ${confirmedInvoice?.total_amount?.toFixed(2) || Number(selectedEvent.total || 0).toFixed(2)}</p>
                                </div> */}



                              {!isInvoiceConfirmed && (
                                <p className="invoice-hint-text">
                                  Please confirm the invoice to enable both download and payment options.
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="invoice-hint-text">
                              The appointment hasn’t started yet. You can confirm the invoice after{' '}
                              {moment(selectedEvent.start).format('MMMM Do, YYYY [at] h:mm A')}.
                            </p>
                          )}
                        </>
                      )}
                      <div className="modal-btns-admin">
                      {selectedEvent?.patient_id && selectedEvent?.doctor_id && (
                        <button
                          className="view-intake-btn-admin"
                          onClick={() =>
                            window.open(
                              `/view-intake/${selectedEvent.patient_id}/${selectedEvent.doctor_id}`,
                              "_blank"
                            )
                          }
                        >
                          View Intake Form
                        </button>
                      )}

                      {["BO", "AP"].includes(selectedEvent.status) && !isInvoiceAlreadyGenerated && (
                        <button
                          className="view-intake-btn-admin"
                          onClick={() => handleCancelAppointment(selectedEvent.id)}
                        >
                          {t("admin_dashboard.cancel_appointment")}
                        </button>
                      )}
                      
                        {/* For AP */}
                        {selectedEvent.status === "AP" && (
                          <>
                            <button
                              className="download-invoice-btn-admin"
                              onClick={() => {
                                const url = FormatUrl(`/download-appointment-invoice/${selectedEvent.id}/`);
                                window.open(url, '_blank');
                              }}
                              disabled={!isInvoiceConfirmed}
                            >
                              {t("admin_dashboard.download_invoice")}
                            </button>

                            <button
                              className="confirm-payment-btn-admin"
                              onClick={handleConfirmPayment}
                              disabled={!isInvoiceConfirmed}
                            >
                              {t("admin_dashboard.submit_payment")}
                            </button>
                          </>
                        )}

                        {/* For PC */}
                        {selectedEvent.status === "PC" && (
                          <button
                            className="download-invoice-btn-admin"
                            onClick={() => {
                              const url = FormatUrl(`/download-appointment-invoice/${selectedEvent.id}/`);
                              window.open(url, '_blank');
                            }}
                          >
                            {t("admin_dashboard.download_invoice")}
                          </button>
                        )}
                        
                        {/* For PW */}
                        {selectedEvent.status === "PW" && (
                          <button
                            className="download-invoice-btn-admin"
                            onClick={() => {
                              const url = FormatUrl(`/download-appointment-invoice/${selectedEvent.id}/?clinic_id=${clinicId}`);
                              window.open(url, '_blank');
                            }}
                          >
                            {t("admin_dashboard.download_invoice")}
                          </button>
                        )}
                        <button className="close-btn-admin" onClick={closeModal}>
                          {t("admin_dashboard.close")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
